/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Page Cashback Control selectors
 *
 * @author Yakov Gushchin
 * @version 1.13.0
 * @since 1.12.0
 */

import { createSelector, OutputSelector } from 'reselect';

import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';

import { makeSelectPageCashbackTranslation } from '../../../modules/internationalization/intSelectors';
import { IPageCashbackTranslation } from '../../../modules/internationalization/types/pages/cashback';
import { IRootReducer } from '../../../reducers/rootReducer';
import { makeSelectIsFeatureWithVersionEnabled } from '../../PageAuth/selectors/authSelectors';
import { IPageCashbackReducer } from '../pageCashbackReducer';
import { ECashbackActions } from '../types/cashbackTypes';
import { makeSelectCashback } from './cashbackSelectors';

/*
* -------------------------------------------
*           Get Service List
* -------------------------------------------
*/

export type TCashbackServiceList =
    Pick<IPageCashbackReducer, 'controlServiceListAvailable' | 'controlServiceListConnected'>
export const makeSelectCashbackServiceList = () => createSelector<IRootReducer,
    IPageCashbackReducer, TCashbackServiceList>([
    makeSelectCashback,
], (state) => ({
    controlServiceListAvailable: state.controlServiceListAvailable,
    controlServiceListConnected: state.controlServiceListConnected,
}));


export const makeSelectCashbackServiceListIsLoading = ():
    OutputSelector<IRootReducer, boolean, (state: IPageCashbackReducer) => boolean> =>
    createSelector(makeSelectCashback, state => state.controlServiceListIsLoading);

export const makeSelectCashbackServiceListIsError = ():
    OutputSelector<IRootReducer, boolean, (state: IPageCashbackReducer) => boolean> =>
    createSelector(makeSelectCashback, state => state.controlServiceListIsError);

/*
* -------------------------------------------
*     Reserve scores for the service
* -------------------------------------------
*/

export const makeSelectCashbackReserveError = ():
    OutputSelector<IRootReducer, string[], (state: IPageCashbackReducer) => string[]> =>
    createSelector(makeSelectCashback, state => state.reserveError);


/*
* -------------------------------------------
*     Action available state
* -------------------------------------------
*/

const makeSelectCashbackActionAvailable = (action: ECashbackActions):
    OutputSelector<IRootReducer, boolean, (state: IPageCashbackReducer) => boolean> =>
    createSelector(makeSelectCashback, state => state.meta.actions.includes(action));

export const makeSelectCashbackReserveAvailable = () =>
    createSelector<IRootReducer, boolean, boolean>(
        [makeSelectCashbackActionAvailable(ECashbackActions.reserve)],
        (isAvailableByAction) => isAvailableByAction,
    );

export const makeSelectCashbackReserveTooltip = () =>
    createSelector<IRootReducer, boolean, IPageCashbackTranslation, string>(
        [
            makeSelectCashbackActionAvailable(ECashbackActions.reserve),
            makeSelectPageCashbackTranslation(),
        ],
        (isAvailableByAction, cashbackTranslation) => {
            const tabControlTranslation = cashbackTranslation.tabControl;
            return !isAvailableByAction
                ? tabControlTranslation.actionTooltipNotByAction
                : tabControlTranslation.btnTooltipNotChosen;
        },
    );


export const makeSelectCashbackAutoSpendingAvailable = () =>
    createSelector<IRootReducer, boolean, boolean>(
        [makeSelectCashbackActionAvailable(ECashbackActions.autoSpending)],
        (isAvailableByAction) => isAvailableByAction);

export const makeSelectCashbackAutoSpendingTooltip = () =>
    createSelector<IRootReducer, boolean, IPageCashbackTranslation, boolean, TCashbackServiceList, boolean, string>(
        [
            makeSelectCashbackAutoSpendingAvailable(),
            makeSelectPageCashbackTranslation(),
            makeSelectCashbackServiceListIsError(),
            makeSelectCashbackServiceList(),
            makeSelectIsFeatureWithVersionEnabled(FeatureNameEnum.CASHBACK, [4], true),
        ],
        (isAvailableByAction, cashbackTranslation, serviceListIsError, serviceList, isCashbackV4Enabled) => {
            const tabControlTranslation = cashbackTranslation.tabControl;
            return !isAvailableByAction
                ? tabControlTranslation.actionTooltipNotByAction
                : (serviceListIsError || !!serviceList.controlServiceListConnected.length || isCashbackV4Enabled)
                    ? '' : tabControlTranslation.autoSpending.tooltipNoServiceList
                ;
        });
